import type { CSSProperties } from 'react';
import { DEFAULT_DOCUMENT_BRANDING } from '@notifi/shared-types/estimate-doc-template';
import type {
  DocumentBranding,
  EstimateDocContentBlock,
  EstimateDocTextStyle,
} from '@notifi/shared-types/estimate-doc-template';

/**
 * Presentation helpers for the block renderer: they read the per-field
 * visibility/relabel config and the per-block label/value styling off a content
 * block and turn them into plain values the JSX consumes. Kept tiny and pure so
 * the renderer stays declarative and the behaviour is unit-testable in isolation.
 */

/**
 * A field prints unless the template explicitly set `visible: false`. Absent
 * config means visible — a fresh template lists no field overrides yet still
 * renders every field.
 */
export function isFieldVisible(block: EstimateDocContentBlock, field: string): boolean {
  return block.fields?.[field]?.visible !== false;
}

/** The author's relabel for a field, or the section's default label. */
export function fieldLabel(
  block: EstimateDocContentBlock,
  field: string,
  fallback: string,
): string {
  const custom = block.fields?.[field]?.label;
  return custom && custom.trim() ? custom : fallback;
}

/** Turn a template text style into inline CSS (web: point size renders as px). */
export function textStyleToCss(style?: EstimateDocTextStyle): CSSProperties {
  if (!style) return {};
  const css: CSSProperties = {};
  if (style.font) css.fontFamily = style.font;
  if (typeof style.size === 'number') css.fontSize = `${style.size}px`;
  if (style.color) css.color = style.color;
  return css;
}

/** Inline CSS for a block's label runs (section labels, table headers, field names). */
export function labelStyle(block: EstimateDocContentBlock): CSSProperties {
  return textStyleToCss(block.style?.label);
}

/** Inline CSS for a block's value runs (the live estimate figures/text). */
export function valueStyle(block: EstimateDocContentBlock): CSSProperties {
  return textStyleToCss(block.style?.value);
}

/**
 * Document-wide theme, applied once at the root as CSS variables + font defaults
 * so every block inherits the tenant's brand without threading branding through
 * each renderer.
 */
// The document names a single brand face (default `Inter`), but that face is not
// guaranteed to be loaded wherever the document renders — the app itself ships a
// different UI font, so a bare `font-family: Inter` silently falls through to the
// browser's default SERIF. Always trail the brand face with a real sans stack so
// the document reads as clean sans (close to the Helvetica the PDF twin prints)
// instead of Times, whether or not the named face is present.
const SANS_FALLBACK =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const withFallback = (font: string) => `${font}, ${SANS_FALLBACK}`;

export function brandingToCssVars(
  // A frozen send-time snapshot could arrive without a branding object; fall back
  // to the system default so the document renders instead of white-screening on
  // an undefined `primaryColor`/`bodyFont`.
  branding: DocumentBranding | null | undefined = DEFAULT_DOCUMENT_BRANDING,
): CSSProperties {
  const b = branding ?? DEFAULT_DOCUMENT_BRANDING;
  return {
    // Custom properties are valid inline styles; typed loosely because
    // CSSProperties has no index signature for `--*` names.
    ['--doc-primary' as string]: b.primaryColor,
    ['--doc-secondary' as string]: b.secondaryColor,
    ['--doc-accent' as string]: b.accentColor,
    ['--doc-body-font' as string]: withFallback(b.bodyFont),
    ['--doc-heading-font' as string]: withFallback(b.headingFont),
    fontFamily: withFallback(b.bodyFont),
    // Body copy stays ink; the brand colour is applied deliberately to accents
    // (eyebrows, the brand rule, the logo mark, the items header, the deposit
    // band and the grand total) via the `--doc-primary` variable — matching the
    // document design, where the brand is a highlight, not the text colour.
    color: '#0a0b0d',
  };
}
